import { yupResolver } from '@hookform/resolvers/yup';
import {
  slugify,
  useCreateCommunity,
  useCreateCommunityMeta,
  useUpdateCommunityLinks,
} from '@sunrise-ui/api-client';
import type { CommunitySize, FundingSource } from '@sunrise-ui/api/profile';
import {
  AuthFlowError,
  Button,
  ButtonVariants,
  Dropdown,
  InputError,
  MultiDropdown,
  TextInput,
  useAuthStore,
  useDynamicStyles,
  useSnackbar,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { isAxiosError } from 'axios';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { array, object, string, type InferType } from 'yup';

const COMMUNITY_SIZE_OPTIONS = [
  { label: 'Less than 500', value: '0-500' },
  { label: '501-1,500', value: '501-1500' },
  { label: '1,501-5,000', value: '1501-5000' },
  { label: 'More than 5,000', value: '5001+' },
];

const FUNDING_SOURCE_OPTIONS = [
  { label: 'Grants', value: 'grants' },
  { label: 'Marketing Sponsorships', value: 'marketing_sponsorships' },
  { label: 'Impact Sponsorships', value: 'impact_sponsorships' },
  { label: 'Ticket Sales', value: 'ticket_sales' },
  { label: 'Lead Generation', value: 'lead_generation' },
];

const schema = object().shape({
  organizationName: string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .max(120, 'Must be 120 characters or less')
    .required('Organization name is required'),
  websiteUrl: string()
    .required('Website URL is required')
    .url('Must be a valid URL'),
  communitySize: string().required('Please select a community size'),
  fundingSources: array()
    .of(string().required())
    .min(1, 'Please select at least one funding source')
    .required('Please select at least one funding source'),
});

type FormValues = InferType<typeof schema>;

export function OrganizationSetupModalScreen() {
  const styles = useDynamicStyles(createStyles);
  const { showSnackbar } = useSnackbar();
  const { setIsOnboarded } = useAuthStore();

  const { mutateAsync: createCommunity } = useCreateCommunity({
    refreshAfterSuccess: true,
  });
  const { mutateAsync: createCommunityMeta } = useCreateCommunityMeta();
  const { mutateAsync: updateCommunityLinks } = useUpdateCommunityLinks();

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      organizationName: '',
      websiteUrl: '',
      communitySize: '',
      fundingSources: [],
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (values: FormValues) => {
    const { organizationName, websiteUrl, communitySize, fundingSources } =
      values;
    const slug = slugify(organizationName);

    let createdCommunityId: string | undefined;

    try {
      const created = await createCommunity({
        name: organizationName,
        slug,
        community_type: 'organization',
      });
      createdCommunityId = created?.id;
    } catch (error) {
      if (error instanceof AuthFlowError) {
        return;
      }
      if (isAxiosError(error) && error.response?.status === 409) {
        showSnackbar({
          text: 'This organization name is already taken. Please try a different name.',
          type: 'error',
        });
      } else {
        showSnackbar({
          text: 'Failed to create organization. Please try again.',
          type: 'error',
        });
      }
      return;
    }

    if (!createdCommunityId) {
      showSnackbar({
        text: 'Failed to create organization. Please try again.',
        type: 'error',
      });
      return;
    }

    try {
      await Promise.all([
        createCommunityMeta({
          communityId: createdCommunityId,
          communitySize: communitySize as CommunitySize,
          fundingSources: fundingSources as FundingSource[],
        }),
        updateCommunityLinks({
          communityId: createdCommunityId,
          input: { website: websiteUrl },
        }),
      ]);

      showSnackbar({ text: 'Organization created', type: 'success' });
      setIsOnboarded(true);
    } catch {
      showSnackbar({
        text: 'Organization created, but some settings may need to be updated manually.',
        type: 'info',
      });
      setIsOnboarded(true);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.flex}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.form}>
          <View>
            <Controller
              control={control}
              name="organizationName"
              render={({ field }) => (
                <TextInput
                  placeholder="Organization Name *"
                  autoCapitalize="words"
                  error={!!errors.organizationName}
                  autoFocus
                  {...field}
                />
              )}
            />
            <InputError fieldError={errors.organizationName} />
          </View>

          <View>
            <Controller
              control={control}
              name="websiteUrl"
              render={({ field }) => (
                <TextInput
                  placeholder="Website URL *"
                  autoCapitalize="none"
                  keyboardType="url"
                  error={!!errors.websiteUrl}
                  {...field}
                />
              )}
            />
            <InputError fieldError={errors.websiteUrl} />
          </View>

          <View>
            <Controller
              control={control}
              name="communitySize"
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  placeholder="Size of Community *"
                  options={COMMUNITY_SIZE_OPTIONS}
                  value={value}
                  onChange={onChange}
                  notClearable
                  error={!!errors.communitySize}
                />
              )}
            />
            <InputError fieldError={errors.communitySize} />
          </View>

          <View>
            <Controller
              control={control}
              name="fundingSources"
              render={({ field: { value, onChange } }) => (
                <MultiDropdown
                  placeholder="Sources of Funding *"
                  options={FUNDING_SOURCE_OPTIONS}
                  values={value}
                  onChange={onChange}
                  error={!!errors.fundingSources}
                />
              )}
            />
            <InputError fieldError={errors.fundingSources} />
          </View>
        </View>
      </ScrollView>
      <View style={styles.footer}>
        <Button
          style={styles.finishButton}
          variant={ButtonVariants.PURPLE}
          size="medium"
          disabled={isSubmitting}
          loading={isSubmitting}
          onPress={handleSubmit(onSubmit)}
        >
          Finish
        </Button>
      </View>
    </KeyboardAvoidingView>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    flex: {
      flex: 1,
      width: '100%',
    },
    scrollContainer: {
      flex: 1,
    },
    scrollContent: {
      gap: isMobile ? 16 : 20,
      alignItems: 'center',
      width: '100%',
      paddingHorizontal: 12,
    },
    form: {
      width: '100%',
      maxWidth: 400,
      gap: 16,
    },
    footer: {
      maxWidth: '100%',
      padding: 12,
      paddingBottom: isMobile ? 36 : 12,
      gap: 16,
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    finishButton: {
      width: isMobile ? 120 : 150,
    },
  });
